import React, { useRef, useEffect } from 'react';

interface VerificationInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export default function VerificationInput({ value, onChange, disabled = false }: VerificationInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Initialize refs array
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);

  const handleChange = (index: number, inputValue: string) => {
    if (!/^[0-9A-F]$/i.test(inputValue) && inputValue !== '') return;

    const newValue = value.split('');
    newValue[index] = inputValue.toUpperCase();
    onChange(newValue.join(''));

    // Move to next input if value is entered
    if (inputValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').toUpperCase();
    const hexCode = pastedData.replace(/[^0-9A-F]/gi, '').slice(0, 6);
    onChange(hexCode);
  };

  return (
    <div className="grid grid-cols-6 gap-3 max-w-[360px] mx-auto">
      {Array(6).fill(0).map((_, index) => (
        <input
          key={index}
          ref={el => inputRefs.current[index] = el}
          type="text"
          maxLength={1}
          value={value[index] || ''}
          onChange={e => handleChange(index, e.target.value)}
          onKeyDown={e => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className="w-full h-14 text-center text-2xl font-mono border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary shadow-sm disabled:bg-gray-100 disabled:text-gray-400 transition-all duration-200"
          style={{ appearance: 'textfield' }}
        />
      ))}
    </div>
  );
}